import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Lock } from "lucide-react";
import { Article } from "@/lib/types";
import api, { timeAgo } from "@/lib/api";
import { TxTitle, useArticlePath, useCardTx } from "@/lib/i18n";
import { homeTrack, SectionHead } from "./shared";

/* NEWSLAYER ORIGINALS — Figma 247:2 "EXACT IMPLEMENTATION AUTHORITY"
   (file VybLFbMYgAYYbpuFcUs4IU, founder-approved redesign, 2026-08).
   Figma controls layout/typography/imagery/strokes/badges; production controls
   the real Originals articles (editorial gate), headlines, timestamps, read
   time, premium status and routes. Artwork below is the exact approved Figma
   imagery committed to /public/originals — never regenerate or substitute. */

const DEFAULT_ART = ["/originals/vault-hero.png", "/originals/support-1.png", "/originals/support-2.png", "/originals/support-3.png"];
const ART_SLOTS = ["flagship", "support-1", "support-2", "support-3"];

/* Admin-managed artwork overrides (Homepage Manager) — null slot = default */
const useOriginalsArt = () =>
  useQuery({
    queryKey: ["originals-artwork"],
    queryFn: async () => (await api.get("/originals/artwork")).data,
    staleTime: 300000,
  });

const INTER = "Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif";

const TYPE_LABELS: Record<string, string> = {
  deep_dive: "Deep Dive",
  market_brief: "The Daily Brief",
  weekly_recap: "Weekly Recap",
  author: "Author Feature",
};

const typeLabel = (a: Article) => TYPE_LABELS[a.content_type || ""] || "Original";

const metaLine = (a: Article, withRead: boolean) =>
  [a.author_name || "NewsLayer", timeAgo(a.published_at),
   withRead && a.read_minutes ? `${a.read_minutes} min read` : null]
    .filter(Boolean).join("   •   ");

const FeatureOriginal = ({ a, art }: { a: Article; art: string }) => {
  const toArticle = useArticlePath();
  const tx = useCardTx(a.slug);
  return (
    <Link to={toArticle(a.slug)} data-testid="originals-featured"
      onClick={() => homeTrack("originals", a.slug)}
      className="group relative block overflow-hidden rounded-[14px] lg:min-h-[529px]"
      style={{
        border: "1px solid transparent",
        background: "linear-gradient(#04050a,#04050a) padding-box, linear-gradient(270deg, rgba(53,59,73,0.9) 0%, rgba(53,59,73,0.75) 55%, rgba(13,214,235,0.72) 82%, rgba(139,92,255,0.82) 100%) border-box",
      }}>
      {/* Approved vault-security artwork (exact Figma asset) */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-56 sm:h-64 lg:inset-y-0 lg:left-auto lg:right-0 lg:h-full lg:w-[42.9%] pointer-events-none">
        <img src={art} alt="" loading="lazy" className="w-full h-full object-cover" />
        <div className="absolute inset-0 lg:hidden bg-gradient-to-t from-[#04050a] via-[#04050a]/40 to-transparent" />
      </div>
      {/* Left-side dark vignette over the artwork (exact Figma stops) */}
      <div aria-hidden className="hidden lg:block absolute inset-y-0 pointer-events-none"
        style={{ left: "38.3%", width: "42.9%", background: "linear-gradient(90deg, #04050a 0%, rgba(4,5,10,0.96) 35%, rgba(4,5,10,0.46) 72%, rgba(4,5,10,0) 100%)" }} />

      <div className="relative z-10 flex flex-col p-5 sm:p-7 pt-48 sm:pt-56 lg:p-0 lg:pl-[34px] lg:pr-[42%] lg:pt-[55px] lg:pb-[35px] lg:h-full" style={{ fontFamily: INTER }}>
        <div className="flex flex-wrap items-center gap-[12px]">
          <span className="inline-flex items-center h-[30px] px-[13px] rounded-[5px] text-white font-bold text-[11px] leading-none tracking-[0.8px] uppercase"
            style={{ background: "linear-gradient(90deg, #5d18e8 0%, #843dff 100%)" }}>
            NewsLayer Original
          </span>
          <span className="inline-flex items-center h-[30px] px-[15px] rounded-[5px] bg-[#070a11] border border-[#295b88] text-[#80b8ff] font-semibold text-[10px] leading-none tracking-[1.1px] uppercase">
            {typeLabel(a)}
          </span>
          {a.access_level === "premium" && (
            <span data-testid="originals-premium-badge"
              className="inline-flex items-center gap-1 h-[30px] px-[14px] rounded-[5px] bg-[#08090d] border border-[#343844] text-[#dadde6] font-semibold text-[10px] leading-none tracking-[1px] uppercase">
              ★ Premium
            </span>
          )}
        </div>
        <h3 className="mt-6 lg:mt-[52px] text-[#f4f6fa] font-bold text-[24px] leading-[31px] sm:text-[32px] sm:leading-[40px] max-w-[455px] line-clamp-3">
          <TxTitle slug={a.slug} title={a.title} />
        </h3>
        <div aria-hidden className="mt-[15px] h-[3px] w-[55px] rounded-[2px]"
          style={{ background: "linear-gradient(90deg, #8b2cff 0%, #1fa7ff 100%)" }} />
        {a.display_dek && (
          <p className="mt-[22px] text-[#a2a8b6] text-[15px] leading-[26px] max-w-[410px] line-clamp-3">
            {a.display_dek}
          </p>
        )}
        <p className="mt-auto pt-5 text-[#787f90] font-semibold text-[9px] leading-[14px] tracking-[1.05px] uppercase whitespace-nowrap overflow-hidden text-ellipsis max-w-[425px]">
          {metaLine(a, true)}
        </p>
        <span className="mt-[30px] inline-flex items-center justify-center gap-2 w-fit h-[51px] px-[28px] rounded-[8px] text-[#f2f5fc] font-semibold text-[16px] transition-opacity group-hover:opacity-90"
          style={{
            border: "1.5px solid transparent",
            background: "linear-gradient(#05060a,#05060a) padding-box, linear-gradient(90deg, #8b2cff 0%, #8b2cff 25%, #00cff8 100%) border-box",
          }}>
          Read full analysis
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
};

const SupportOriginal = ({ a, art }: { a: Article; art: string }) => {
  const toArticle = useArticlePath();
  return (
    <Link to={toArticle(a.slug)} data-testid={`originals-row-${a.slug}`}
      onClick={() => homeTrack("originals", a.slug)}
      className="group relative flex items-center gap-4 lg:gap-[19px] p-4 lg:p-[18px] rounded-[12px] flex-1 lg:min-h-[169px]"
      style={{
        fontFamily: INTER,
        border: "1px solid transparent",
        background: "linear-gradient(#05060b,#05060b) padding-box, linear-gradient(90deg, #2b303c 0%, #2b303c 78%, rgba(75,82,98,0.65) 100%) border-box",
      }}>
      {/* Approved Figma thumbnail (exact asset by slot) */}
      <span className="block w-[120px] h-[90px] sm:w-[176px] sm:h-[132px] shrink-0 rounded-[7px] overflow-hidden">
        <img src={art} alt="" loading="lazy"
          className="w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-300 group-hover:scale-[1.03]" />
      </span>
      <span className="min-w-0 flex-1 flex flex-col self-stretch py-[6px]">
        <span className="flex items-center justify-between gap-2 min-w-0">
          <span className="text-[#a16bff] font-semibold text-[10px] leading-[14px] tracking-[1.3px] uppercase whitespace-nowrap truncate min-w-0">
            NewsLayer Original
            {a.access_level === "premium" && <Lock className="h-2.5 w-2.5 inline ml-1.5 -mt-0.5" />}
          </span>
          <span className="hidden sm:inline-flex items-center h-[25px] px-[13px] rounded-[5px] bg-[#070a10] border border-[#295b88] text-[#80b8ff] font-semibold text-[9px] leading-none tracking-[1px] uppercase shrink-0 whitespace-nowrap max-w-[45%] overflow-hidden text-ellipsis">
            {typeLabel(a)}
          </span>
        </span>
        <span className="block mt-[10px] text-[#f2f5fc] font-bold text-[15px] leading-[20px] sm:text-[17px] sm:leading-[22px] line-clamp-3 max-w-[298px]">
          <TxTitle slug={a.slug} title={a.title} />
        </span>
        <span className="block mt-auto pt-2 text-[#777e8d] font-semibold text-[8px] leading-[12px] tracking-[0.9px] uppercase whitespace-nowrap overflow-hidden text-ellipsis">
          {metaLine(a, false)}
        </span>
      </span>
    </Link>
  );
};

export const OriginalsSection = ({ originals }: { originals: Article[] }) => {
  const { data: artCfg } = useOriginalsArt();
  if (!originals?.length) return null;
  const [featured, ...rest] = originals;
  const artFor = (i: number) => artCfg?.[ART_SLOTS[i]] || DEFAULT_ART[i];
  return (
    <section data-testid="originals-section" className="relative mt-10 sm:mt-14">
      {/* Subtle vertical pinline texture (Figma: 1px rgba(37,42,54,0.1) every 24px) */}
      <div aria-hidden className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(37,42,54,0.1) 0px, rgba(37,42,54,0.1) 1px, transparent 1px, transparent 24px)" }} />
      {/* Violet / cyan floor glows (exact Figma ellipses) */}
      <div aria-hidden className="absolute rounded-full pointer-events-none blur-[70px]"
        style={{ left: "25.1%", top: "76.6%", width: "42.3%", height: 159, background: "rgba(113,40,255,0.24)" }} />
      <div aria-hidden className="absolute rounded-full pointer-events-none blur-[70px]"
        style={{ left: "25.1%", top: "17.4%", width: "42.3%", height: 159, background: "rgba(113,40,255,0.24)" }} />
      <div aria-hidden className="absolute rounded-full pointer-events-none blur-[65px]"
        style={{ left: "51%", top: "79.8%", width: "24.1%", height: 130, background: "rgba(13,214,235,0.12)" }} />
      <div aria-hidden className="absolute rounded-full pointer-events-none blur-[65px]"
        style={{ left: "51%", top: "20.5%", width: "24.1%", height: 130, background: "rgba(13,214,235,0.12)" }} />

      {/* Header — shared SectionHead (Figma 247:2 type system, site-wide) */}
      <div className="relative">
        <SectionHead label="From the NewsLayer desk" title="NewsLayer Originals"
          sub="Original reporting from NewsLayer Reporters & Bloggers. Exclusive analysis. NewsLayer intelligence."
          cta="View all originals" to="/originals" testid="originals-cta" module="originals" />
      </div>

      <div className={`relative grid grid-cols-1 gap-[14px] ${rest.length > 0 ? "lg:grid-cols-[minmax(0,770fr)_minmax(0,537fr)]" : ""}`}>
        <FeatureOriginal a={featured} art={artFor(0)} />
        {rest.length > 0 && (
          <div className="flex flex-col gap-[14px]">
            {rest.slice(0, 3).map((a, i) => <SupportOriginal key={a.slug} a={a} art={artFor(i + 1)} />)}
          </div>
        )}
      </div>
    </section>
  );
};
